import React from "react";
import { parseReport } from "../../../../utils/reportParser";
import ReportDTO, { GenericReport } from "../../../../models/finance/Reports";
import { FinanceType, FinanceTypeDef } from "../../../../models/finance/FinanceType";
import { Avatar, Box, Chip, Typography, useTheme, Grid, Stack, alpha } from "@mui/material";
import MonthlyReportsCategoryBreakdownListComponent, {
  MonthlyCategoryBreakdownRow,
} from "./MonthlyReportsCategoryBreakdownListComponent";
import SurfaceCard from "../../../common/ui/SurfaceCard";
import { useCurrencyFormatter } from "../../../../contexts/CurrencyContext";
import { BarChartOutlined, CalendarMonthOutlined, ShowChartOutlined } from "@mui/icons-material";

interface MonthlyReportsComponentPropos {
  report: ReportDTO;
  type: FinanceTypeDef;
  year: number;
  month: string;
}

const MonthlyReportsComponent: React.FC<MonthlyReportsComponentPropos> = ({
  report,
  type,
  year,
  month,
}) => {
  const theme = useTheme();
  const { formatCurrencyNumber, formatCompactCurrencyNumber } = useCurrencyFormatter();
  const genericReport: GenericReport = parseReport(
    report,
    type
  );

  const isExpense = type === FinanceType.EXPENSE;

  const monthlyReportsPerCategory: MonthlyCategoryBreakdownRow[] =
    Object.entries(genericReport.valuePerCategory).map(([categoryName, actual]) => ({
      categoryName,
      actual,
    }));

  const sortedCategories = [...monthlyReportsPerCategory].sort((a, b) => b.actual - a.actual);
  const topCategory = sortedCategories[0];

  const activeDays = Object.values(genericReport.valuePerDay).filter((amount) => amount > 0).length;
  const trackedDays = Math.max(Object.keys(genericReport.valuePerDay).length, 1);
  const dailyAverage = genericReport.totalAmount / trackedDays;
  const concentration = topCategory && genericReport.totalAmount > 0
    ? (topCategory.actual / genericReport.totalAmount) * 100
    : 0;

  const themeColor = isExpense ? theme.palette.error : theme.palette.success;
  const sectionTint = alpha(themeColor.main, 0.12);
  const sectionBorder = alpha(themeColor.main, 0.28);
  const sectionTitle = isExpense ? "Spending Summary" : "Income Summary";
  const sectionSubtitle = isExpense
    ? `How ${month} ${year} spending is distributed and trending`
    : `How ${month} ${year} income is distributed and trending`;

  return (
    <Box sx={{ width: "100%" }}>
      <SurfaceCard sx={{ mb: 3.2, borderRadius: "14px", border: `1px solid ${sectionBorder}` }}>
        <Box
          sx={{
            p: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            background: `linear-gradient(135deg, ${sectionTint} 0%, ${alpha(theme.palette.background.paper, 0.35)} 100%)`,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: themeColor.main, fontSize: "1.1rem", mb: 0.4 }}>
            {sectionTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.85rem" }}>
            {sectionSubtitle}
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 2.4 } }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${alpha(themeColor.main, 0.3)}`,
                  background: alpha(themeColor.main, 0.12),
                  height: "100%",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.8 }}>
                  <Avatar
                    sx={{
                      width: 30,
                      height: 30,
                      bgcolor: alpha(themeColor.main, 0.2),
                      color: themeColor.main,
                    }}
                  >
                    <BarChartOutlined fontSize="small" />
                  </Avatar>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Total {isExpense ? "Spent" : "Received"}
                  </Typography>
                </Stack>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: themeColor.main,
                    fontSize: { xs: "1.45rem", sm: "1.7rem" },
                    lineHeight: 1.15,
                    wordBreak: "break-word",
                    mb: 0.5,
                  }}
                >
                  {formatCompactCurrencyNumber(genericReport.totalAmount)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                  {formatCurrencyNumber(genericReport.totalAmount)}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                  background: alpha(theme.palette.background.default, 0.5),
                  height: "100%",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Avatar
                    sx={{
                      width: 30,
                      height: 30,
                      bgcolor: alpha(theme.palette.info.main, 0.2),
                      color: theme.palette.info.light,
                    }}
                  >
                    <ShowChartOutlined fontSize="small" />
                  </Avatar>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Category Concentration
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.6 }}>
                  {topCategory
                    ? `${topCategory.categoryName} accounts for ${concentration.toFixed(1)}%`
                    : "No category concentration data available."}
                </Typography>
                <Chip
                  size="small"
                  icon={<CalendarMonthOutlined />}
                  label={`${activeDays} active days | avg ${formatCompactCurrencyNumber(dailyAverage)}/day`}
                  sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.16),
                    color: theme.palette.primary.light,
                    '& .MuiChip-label': { whiteSpace: 'normal' },
                    height: "auto",
                    py: 0.4,
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </SurfaceCard>

      <SurfaceCard sx={{ mb: 3.2, borderRadius: "14px", border: `1px solid ${sectionBorder}` }}>
        <Box sx={{ 
          p: 3, 
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: `linear-gradient(135deg, ${sectionTint} 0%, ${alpha(theme.palette.background.paper, 0.35)} 100%)`,
        }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: themeColor.main, fontSize: '1.1rem', mb: 0.4 }}>
            Category Breakdown
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
            Ranked category contribution with share and amount for {month} {year}
          </Typography>
        </Box>
        <Box sx={{ p: { xs: 1.6, sm: 2.4 } }}>
          <MonthlyReportsCategoryBreakdownListComponent
            year={year}
            month={month}
            type={type}
            rows={monthlyReportsPerCategory}
          />
        </Box>
      </SurfaceCard>

    </Box>
  );
};

export default MonthlyReportsComponent;
